import { $getSelection, $isRangeSelection } from "lexical";
import {
    $deleteTableColumnAtSelection,
    $deleteTableRowAtSelection,
    $getTableNodeFromLexicalNodeOrThrow,
    $insertTableColumnAtSelection,
    $insertTableRowAtSelection,
} from "@lexical/table";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    BetweenHorizontalEnd,
    BetweenHorizontalStart,
    BetweenVerticalEnd,
    BetweenVerticalStart,
    Columns2,
    Rows2,
    Trash2,
} from "lucide-react";

import { useButtonState } from "../useButtonState";

export function useIsInTable(): boolean {
    return useButtonState(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        let node = selection.anchor.getNode();
        while (node) {
            if (node.getType() === "tablecell") return true;
            const parent = node.getParent();
            if (!parent) return false;
            node = parent;
        }
        return false;
    });
}

function withTableSelection(editor: ReturnType<typeof useLexicalComposerContext>[0], op: () => void) {
    editor.update(() => {
        op();
    });
}

export function InsertRowAboveButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $insertTableRowAtSelection(false))}
            title="Insert row above"
            aria-label="Insert row above"
        >
            <BetweenHorizontalStart size={16} />
        </button>
    );
}

export function InsertRowBelowButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $insertTableRowAtSelection(true))}
            title="Insert row below"
            aria-label="Insert row below"
        >
            <BetweenHorizontalEnd size={16} />
        </button>
    );
}

export function InsertColumnLeftButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $insertTableColumnAtSelection(false))}
            title="Insert column left"
            aria-label="Insert column left"
        >
            <BetweenVerticalStart size={16} />
        </button>
    );
}

export function InsertColumnRightButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $insertTableColumnAtSelection(true))}
            title="Insert column right"
            aria-label="Insert column right"
        >
            <BetweenVerticalEnd size={16} />
        </button>
    );
}

export function DeleteRowButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $deleteTableRowAtSelection())}
            title="Delete row"
            aria-label="Delete row"
        >
            <Rows2 size={16} />
        </button>
    );
}

export function DeleteColumnButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => withTableSelection(editor, () => $deleteTableColumnAtSelection())}
            title="Delete column"
            aria-label="Delete column"
        >
            <Columns2 size={16} />
        </button>
    );
}

export function DeleteTableButton() {
    const [editor] = useLexicalComposerContext();
    return (
        <button
            type="button"
            className="rte-toolbar-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
                withTableSelection(editor, () => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection)) return;
                    const anchorNode = selection.anchor.getNode();
                    try {
                        const tableNode = $getTableNodeFromLexicalNodeOrThrow(anchorNode);
                        tableNode.remove();
                    } catch {
                        /* not in a table */
                    }
                })
            }
            title="Delete table"
            aria-label="Delete table"
        >
            <Trash2 size={16} />
        </button>
    );
}
